import React, { useEffect, useState } from 'react';
import { array, bool, func, number, object, string, oneOfType } from 'prop-types';
import { useDispatch } from 'react-redux';
import cloneDeep from 'lodash/cloneDeep';
import forEach from 'lodash/forEach';
import toString from 'lodash/toString';
import classnames from 'classnames';
import AdvancedTable from 'ps-components/AdvancedTable/AdvancedTable';
import InPlaceIndicator from 'ps-components/LoadingIndicator/InPlaceIndicator/InPlaceIndicator';
import {
  getGenericSurveyRow,
  getTransformedColumns
} from 'ps-components/AdvancedTable/GenericGrid/GenericGridFunctions';
import { jobMatchGridActions } from '../../LandingPage/JobMatchDetail/JobMatchGridCreate';
import Placeholder from '../../Placeholder/Placeholder';
import GG_DEFAULTS from '../../../../lib/genericGridDefaults';
import './ParticipationMatchesGrid.scss';

// UI custom filter: Filter by hasData, by search term, and preferences
function filterAndSearch(surveyJobs, showWithData, filterText, data, terms) {
  if (surveyJobs?.length === 0) {
    return [];
  }

  let newData = cloneDeep(data);
  const termsArray = terms.map(e => e.term);
  const hasDataIndex = termsArray.indexOf('hasData');

  // only filter on these terms when searching with input
  const validSearchIndexes = [
    termsArray.indexOf('surveyCode'),
    termsArray.indexOf('surveyCurrency'),
    termsArray.indexOf('surveyJobCode'),
    termsArray.indexOf('surveyTitle'),
    termsArray.indexOf('surveyJobTitle'),
    termsArray.indexOf('jobTitle')
  ];

  if (data) {
    const lower = toString(filterText).toLowerCase();

    newData = data.filter(row => {
      let match = false;
      for (let i = 0; i < row.length; i++) {
        if (validSearchIndexes.includes(i)) {
          const thisLower = toString(row[i]).toLowerCase();

          if (thisLower.includes(lower)) {
            match = true;
          }
        }
      }

      // Omit this row from the UI if it does not match the search input filter
      // or if the hasData value for this row does not match the current
      // toggle for showing rows with or without data
      if (!match || (row[hasDataIndex] || false) !== showWithData) {
        return false;
      }

      return true;
    });
  }

  return newData || [];
}

// combine grid data result and survey job recommendations
function combineData(surveyJobs, grid, showWithData) {
  // If there are no results for selected tab, then skip combination logic below
  if (!surveyJobs || surveyJobs.length === 0 || !grid) {
    return grid;
  }

  const newGrid = cloneDeep(grid);
  let count = grid?.terms?.length;

  if (!showWithData) {
    newGrid.terms = [];
    count = 0;
  }

  // Create terms from recommender results and combine with grid terms
  surveyJobs?.forEach(surveyJob => {
    forEach(surveyJob, (value, key) => {
      const foundTerm = newGrid?.terms?.find(t => t.term === key);
      if (!foundTerm) {
        newGrid?.terms.push({
          id: count,
          dataType: 'text',
          term: key,
          termType: 'field',
          displayName: key,
          name: key,
          metadata: {
            width: 100
          }
        });
        count++;
      }
    });
  });

  const cutIdIndex = newGrid?.terms?.find(t => t.term === GG_DEFAULTS.CUT_ID)?.id;

  if (!showWithData) {
    newGrid.data = [];
    let index = -1;

    surveyJobs?.forEach(surveyJob => {
      if (!surveyJob.hasData) {
        // build new row in the same order as terms created above
        const newDataRow = [];
        index++;

        forEach(surveyJob, (value, key) => {
          const newDataRowIndex = newGrid?.terms?.findIndex(t => t.term === key);
          if (newDataRowIndex > -1) {
            newDataRow[newDataRowIndex] = value;
          }
        });

        newGrid.data[index] = newDataRow;
      }
    });
  } else {
    forEach(surveyJobs, surveyJob => {
      if (surveyJob.hasData) {
        forEach(newGrid.data, gridData => {
          if (gridData[cutIdIndex] === surveyJob.cutId) {
            forEach(surveyJob, surveyJobValue => {
              gridData.push(surveyJobValue);
            });
          }
        });
      }
    });
  }

  return newGrid;
}

ParticipationMatchesGrid.propTypes = {
  actions: object,
  addMatch: func,
  addSelectedCutID: func,
  benchmarkYear: number,
  counts: object,
  currency: string,
  deleteMatch: func,
  displayIn: string,
  filterText: string,
  getSurveyJobDetailsPageHref: func,
  grid: object,
  groupId: string,
  hideSelect: bool,
  isMatchDeleting: bool,
  isPrint: bool,
  jobId: oneOfType([number, string]),
  loadingId: number,
  masterAccountId: string,
  monetaryRounding: number,
  numericRounding: number,
  onBack: func,
  onDataCutClick: func,
  onSurveyJobTitleClick: func,
  percentRounding: number,
  removeSelectedCutID: func,
  renderPopover: bool,
  selectedRowId: number,
  selectedSurveys: array,
  selectedViewPk: number,
  settings: object,
  setTotalFilteredDataCount: func,
  setTotalFilteredNoDataCount: func,
  showWithData: bool,
  stickyHeader: bool,
  surveyJobs: object,
  surveyViewName: string,
  togglePopover: func,
  updateModifiedDate: func
};

ParticipationMatchesGrid.defaultProps = {
  isPrint: false
};

function ParticipationMatchesGrid(props) {
  const [addedMatches, setAddedMatches] = useState([]);
  const dispatch = useDispatch();

  const jobsWithData = props.surveyJobs?.jobs
    ?.filter(j => j.hasData)
    ?.map(j => {
      return {
        cutId: j.cutId
      };
    });

  if (props.surveyJobs == null) {
    return null;
  }

  useEffect(() => {
    if (addedMatches.length > 0) {
      setAddedMatches([]);
    }
  }, [props.selectedSurveys]);

  const newGridWithData = combineData(props.surveyJobs?.jobs, props.grid, true);
  const filteredWithData = filterAndSearch(
    props.surveyJobs?.jobs,
    true,
    props.filterText,
    newGridWithData.data,
    newGridWithData.terms
  );
  const newGridNoData = combineData(props.surveyJobs?.jobs, props.grid, false);
  const filteredNoData = filterAndSearch(
    props.surveyJobs?.jobs,
    false,
    props.filterText,
    newGridNoData.data,
    newGridNoData.terms
  );

  // Filter and determine counts to display in tabs
  const newGrid = props.showWithData ? newGridWithData : newGridNoData;
  const filteredData = props.showWithData ? filteredWithData : filteredNoData;
  props.setTotalFilteredDataCount(filteredWithData.length);
  props.setTotalFilteredNoDataCount(filteredNoData.length);

  // Checks for no results after searching
  const noResultsAfterSearching =
    !props.surveyJobs?.loading && !props.grid?.loading && filteredData?.length === 0 && props.filterText !== '';

  const noResultsWithoutFilters = !props.surveyJobs?.loading && !props.grid?.loading && filteredData?.length === 0;

  // Show if no results shown after searching
  const noResultsAfterSearchingPlaceholder = (
    <div
      id="participation-matches__no-matches-container"
      className="participation-matches__no-results-container-search"
    >
      <Placeholder
        className="pxl-text-small pxl-text-muted"
        primaryText={
          <span>
            No search results for <b>“ {props.filterText} ”</b>
          </span>
        }
        secondaryText="Please try different keywords"
        showText={true}
      />
    </div>
  );

  const noResultsWithoutFiltersPlaceholder = (
    <div
      id="participation-matches__no-matches-container"
      className="participation-matches__no-results-container-search"
    >
      <Placeholder
        className="pxl-text-small pxl-text-muted"
        primaryText={
          <span className="participation-matches__no-matches-msg">{`No participation matches ${
            props.showWithData ? 'with data' : 'without data'
          } found`}</span>
        }
        showText={true}
      />
    </div>
  );

  const showPlaceholder = noResultsAfterSearching || noResultsWithoutFilters;
  let placeholderToShow;
  if (noResultsAfterSearching) {
    placeholderToShow = noResultsAfterSearchingPlaceholder;
  } else if (noResultsWithoutFilters) {
    placeholderToShow = noResultsWithoutFiltersPlaceholder;
  }

  async function onClick(cutId) {
    if (isChecked(cutId)) {
      await props.deleteMatch(props.jobId, [cutId], null, props.benchmarkYear, false, null);
      props.removeSelectedCutID(cutId);

      const newAddedMatches = addedMatches;
      newAddedMatches.splice(newAddedMatches.indexOf(cutId), 1);

      setAddedMatches(newAddedMatches);
    } else {
      await props.addMatch(props.jobId, cutId, null, false, props.benchmarkYear, null);
      props.addSelectedCutID(cutId);

      const newAddedMatches = addedMatches;
      newAddedMatches.push(cutId);

      setAddedMatches(newAddedMatches);
    }

    dispatch(jobMatchGridActions.setIsStale(props.jobId, true));
    props.updateModifiedDate(props.jobId, props.benchmarkYear);
  }

  function useSelectAccessor(row) {
    return row.allCells.find(cell => cell.column.id === GG_DEFAULTS.CUT_ID)?.value;
  }

  function isRowLoading(row, loadingId) {
    return row.allCells.find(cell => cell.column.id === GG_DEFAULTS.CUT_ID)?.value === loadingId;
  }

  function isChecked(cutId) {
    return props.selectedSurveys?.includes(cutId);
  }

  const { togglePopover, selectedRowId, renderPopover, counts, showWithData, filterText } = props;

  let noDataText = `No surveys found for your search entry "${filterText}"`;
  if (counts.data <= 0 && counts.noData > 0 && showWithData) {
    noDataText = 'Your current pricing matches do not have any suggested Participation Matches';
  } else if (counts.data > 0 && counts.noData <= 0 && !showWithData) {
    noDataText = 'No suggested Participation Matches without data found';
  }

  if (jobsWithData.length > 0 && (props.grid?.loading || !props.grid?.loaded)) {
    return <InPlaceIndicator />;
  }

  return (
    <div
      className={classnames('generic-grid__container participation-matches__grid-container', {
        'no-data-grid-container': !props.showWithData
      })}
    >
      {showPlaceholder ? (
        placeholderToShow
      ) : (
        <AdvancedTable
          {...newGrid.tableProps}
          getDataRow={(row, idx) =>
            getGenericSurveyRow(row, idx, {
              isRowLoading: isRowLoading(row, props.loadingId),
              isRowDeleting: props.isMatchDeleting,
              loadingText: 'Adding Match to Pricing...',
              deletingText: 'Removing Match from Pricing...',
              togglePopover,
              selectedRowId,
              renderPopover,
              addedMatches,
              onDataCutClick: props.onDataCutClick,
              onSurveyJobTitleClick: props.onSurveyJobTitleClick,
              getSurveyJobDetailsPageHref: props.getSurveyJobDetailsPageHref,
              numericRounding: props.numericRounding,
              percentRounding: props.percentRounding,
              monetaryRounding: props.monetaryRounding,
              isPrint: props.isPrint
            })
          }
          rowData={filteredData}
          totalCount={filteredData.length}
          columnHeaders={getTransformedColumns(
            newGrid.termGroups,
            newGrid.terms,
            filteredData,
            newGrid.defaultLeftColumns,
            newGrid.defaultRightColumns
          )}
          rowIndexPrefix={`participation-matches-${props.showPurchasedSurveys ? 'withData' : 'withNoData'}`}
          {...props.actions}
          stickyHeader={props.stickyHeader || false}
          useSelectAccessor={props.showWithData ? useSelectAccessor : null}
          isChecked={isChecked}
          onCheck={onClick}
          loadingId={props.loadingId}
          centerAlignHeaders
          dataLoading={props.loading}
          noDataText={noDataText}
          loadingText={'Loading Survey Job Data'}
          hideSelect={props.hideSelect}
          isPrint={props.isPrint}
        />
      )}
    </div>
  );
}

export default ParticipationMatchesGrid;
